import { apiServiceInstance } from "@/core/helpers/utils";
import {
    ApiResponse,
    IDonViPdfExtra,
    IVatTuDinhMucCoDinhResponseBase,
    IVatTuHopDongResponseBase,
    IVatTuResponseBase,
    IVatTuSearchInKhoResponseBase,
    IVatTuTrongKhoResponseBase,
    MessageItemResponse,
    PaginationResponseBase,
} from "@/core/interfaces/ApiResponses";
import { IDanhSachVatTuFilterParams } from "../interfaces/ApiRequest.interface";
import {
    ICreateOrUpdateVatTuPayload,
    IPaginationParams,
} from "../interfaces/Models";

export class VatTuService {
  static async listAllByDanhMuc(
    id_dm_vat_tu = 0,
    per_page = 100
  ): Promise<{
    items: IVatTuResponseBase[];
    extra?: IDonViPdfExtra;
  }> {
    const allItems: IVatTuResponseBase[] = [];
    let page = 1;
    let total = Infinity;
    let extra: IDonViPdfExtra | undefined;

    while (allItems.length < total) {
      const { data: response } = await VatTuService.list({
        current_page: page,
        ...(id_dm_vat_tu && { id_dm_vat_tu }),
        per_page,
      });

      const {
        data: { data, total: totalItems },
      } = response;

      if (page === 1) {
        extra = response.extra as IDonViPdfExtra | undefined;
      }

      total = totalItems;
      allItems.push(...data);

      if (!data.length) break;
      page += 1;
    }

    return { items: allItems, extra };
  }

  static list({
    current_page,
    id_dm_vat_tu,
    id_don_vi,
    s,
    per_page,
    vat_tu_ids,
    id_kho,
    is_valid,
    loai_vat_tu,
    thu_hoi,
    cong_ty_duyet,
  }: IDanhSachVatTuFilterParams): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuResponseBase>>;
  }> {
    let paramVatuTuArr = "";

    if (vat_tu_ids && vat_tu_ids.length) {
      paramVatuTuArr = vat_tu_ids.map((id) => `id_vat_tu[]=${id}`).join("&");
    }

    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/vat-tu/danh-sach?${paramVatuTuArr}`,
      {
        params: {
          page: current_page || 1,
          ...(s && {
            s,
          }),
          ...(id_kho && {
            id_kho,
          }),
          ...(id_dm_vat_tu && {
            id_dm_vat_tu,
          }),
          ...(id_don_vi && {
            id_don_vi,
          }),
          ...(typeof loai_vat_tu !== "undefined" && {
            loai_vat_tu,
          }),
          ...(typeof is_valid !== "undefined" &&
            [-1, 0, 1].includes(is_valid) && {
              is_valid,
            }),
          ...(typeof thu_hoi !== "undefined" &&
            [0, 1].includes(thu_hoi) && {
              thu_hoi,
            }),
          ...(typeof cong_ty_duyet !== "undefined" &&
            [0, 1].includes(cong_ty_duyet) && {
              cong_ty_duyet,
            }),
        },
        headers: {
          perpage: per_page || 50,
        },
      }
    );
  }

  static getVatTuItemsByKhoId({
    idKho,
    page,
    perPage,
    searchTerm,
    thu_hoi,
    cong_ty_duyet,
  }: {
    idKho: number;
    page: number;
    perPage: number;
    searchTerm?: string;
    thu_hoi?: 0 | 1;
    cong_ty_duyet?: 0 | 1;
  }): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuTrongKhoResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/kho/chi-tiet/${idKho}/vat-tu`,
      {
        params: {
          page,
          ...(searchTerm && {
            s: searchTerm,
          }),
          ...(typeof thu_hoi !== "undefined" &&
            [0, 1].includes(thu_hoi) && {
              thu_hoi,
            }),
          ...(typeof cong_ty_duyet !== "undefined" &&
            [0, 1].includes(cong_ty_duyet) && {
              cong_ty_duyet,
            }),
        },
        headers: {
          perpage: perPage,
        },
      }
    );
  }

  static async createOrUpdate({
    cong_ty_duyet,
    don_vi_tinh,
    dac_tinh,
    note,
    id,
    id_danh_muc,
    ma_vat_tu,
    muc_du_tru_toi_da,
    muc_du_tru_toi_thieu,
    ten_vat_tu,
    thu_hoi,
  }: ICreateOrUpdateVatTuPayload): Promise<{
    data: ApiResponse<IVatTuResponseBase | MessageItemResponse>;
  }> {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      "Content-Type"
    ] = "multipart/form-data";

    const url = id ? `/api/v1/vat-tu/cap-nhat/${id}` : "/api/v1/vat-tu/tao-moi";
    const bodyFormData = new FormData();
    bodyFormData.append("vatTu[ten_vat_tu]", ten_vat_tu);
    bodyFormData.append("vatTu[don_vi_tinh]", don_vi_tinh);
    bodyFormData.append("vatTu[dac_tinh]", dac_tinh ?? "");
    bodyFormData.append("vatTu[note]", note ?? "");
    bodyFormData.append(
      "vatTu[muc_du_tru_toi_thieu]",
      muc_du_tru_toi_thieu.toString()
    );
    bodyFormData.append(
      "vatTu[muc_du_tru_toi_da]",
      muc_du_tru_toi_da.toString()
    );
    bodyFormData.append("vatTu[thu_hoi]", thu_hoi.toString());
    bodyFormData.append("vatTu[cong_ty_duyet]", cong_ty_duyet.toString());
    bodyFormData.append("vatTu[id_danh_muc][]", id_danh_muc.toString());

    if (ma_vat_tu && !id) {
      bodyFormData.append("vatTu[ma_vat_tu]", ma_vat_tu);
    }

    return apiServiceInstance().vueInstance.axios.post(url, bodyFormData);
  }

  static create(
    name: string,
    unitString: string,
    minimumReserve: number,
    maximumReserve: number,
    recall: boolean,
    isApprovedByComp: boolean,
    accountId = 0,
    maDanhMucVatTu: number,
    thongTinTaiKhoan: {
      ma_tai_khoan: number;
      ma_chi_tiet: string;
      ten_tk: string;
    },
    soDuDauNam = 0
  ): Promise<{
    data: ApiResponse<IVatTuResponseBase | MessageItemResponse>;
  }> {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      "Content-Type"
    ] = "multipart/form-data";

    const bodyFormData = new FormData();

    bodyFormData.append("vatTu[ten_vat_tu]", name);

    if (unitString) {
      bodyFormData.append("vatTu[don_vi_tinh]", unitString);
    }

    bodyFormData.append(
      "vatTu[muc_du_tru_toi_thieu]",
      minimumReserve.toString()
    );

    bodyFormData.append("vatTu[muc_du_tru_toi_da]", maximumReserve.toString());
    bodyFormData.append("vatTu[thu_hoi]", recall ? "1" : "0");
    bodyFormData.append("vatTu[cong_ty_duyet]", isApprovedByComp ? "1" : "0");
    bodyFormData.append("vatTu[id_tai_khoan]", accountId.toString());

    if (maDanhMucVatTu > 0) {
      bodyFormData.append("vatTu[id_danh_muc][]", maDanhMucVatTu.toString());
    }

    if (thongTinTaiKhoan.ma_chi_tiet) {
      bodyFormData.append("vatTu[ma_ct]", thongTinTaiKhoan.ma_chi_tiet);
    }

    if (thongTinTaiKhoan.ma_tai_khoan) {
      bodyFormData.append(
        "vatTu[ma_tk]",
        thongTinTaiKhoan.ma_tai_khoan.toString()
      );
    }

    if (thongTinTaiKhoan.ten_tk) {
      bodyFormData.append("vatTu[ten_tk]", thongTinTaiKhoan.ten_tk.toString());
    }

    if (soDuDauNam) {
      bodyFormData.append("vatTu[so_du_dau_nam]", soDuDauNam.toString());
    }

    return apiServiceInstance().vueInstance.axios.post(
      "/api/v1/vat-tu/tao-moi",
      bodyFormData
    );
  }

  static fetchSupplyDetail(id: number): Promise<{
    data: ApiResponse<IVatTuResponseBase>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/vat-tu/chi-tiet/${id}`
    );
  }

  static updateSupply(
    id: number,
    name: string,
    unitString: string,
    minimumReserve: number,
    maximumReserve: number,
    recall: boolean,
    isApprovedByComp: boolean,
    accountId = 0,
    maDanhMucVatTu: number,
    thongTinTaiKhoan: {
      ma_tai_khoan: number;
      ma_chi_tiet: string;
      ten_tk: string;
    },
    soDuDauNam = 0
  ): Promise<{
    data: ApiResponse<IVatTuResponseBase>;
  }> {
    apiServiceInstance().vueInstance.axios.defaults.headers.common[
      "Content-Type"
    ] = "multipart/form-data";

    const bodyFormData = new FormData();

    bodyFormData.append("vatTu[ten_vat_tu]", name);

    if (unitString) {
      bodyFormData.append("vatTu[don_vi_tinh]", unitString);
    }

    bodyFormData.append(
      "vatTu[muc_du_tru_toi_thieu]",
      minimumReserve.toString()
    );

    bodyFormData.append("vatTu[muc_du_tru_toi_da]", maximumReserve.toString());

    bodyFormData.append("vatTu[thu_hoi]", recall ? "1" : "0");

    bodyFormData.append("vatTu[cong_ty_duyet]", isApprovedByComp ? "1" : "0");

    bodyFormData.append("vatTu[id_tai_khoan]", accountId.toString());

    if (maDanhMucVatTu > 0) {
      bodyFormData.append("vatTu[id_danh_muc][]", maDanhMucVatTu.toString());
    }

    if (thongTinTaiKhoan.ma_chi_tiet) {
      bodyFormData.append("vatTu[ma_ct]", thongTinTaiKhoan.ma_chi_tiet);
    }

    if (thongTinTaiKhoan.ma_tai_khoan) {
      bodyFormData.append(
        "vatTu[ma_tk]",
        thongTinTaiKhoan.ma_tai_khoan.toString()
      );
    }

    if (thongTinTaiKhoan.ten_tk) {
      bodyFormData.append("vatTu[ten_tk]", thongTinTaiKhoan.ten_tk.toString());
    }

    if (soDuDauNam) {
      bodyFormData.append("vatTu[so_du_dau_nam]", soDuDauNam.toString());
    }
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/vat-tu/cap-nhat/${id}`,
      bodyFormData
    );
  }

  static search(
    term: string,
    categoryId = 0
  ): Promise<{
    data: {
      data: IVatTuResponseBase[];
    };
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/vat-tu/tim-kiem?s=${term}${
        categoryId && categoryId > 0 ? `&cid=${categoryId}` : ""
      }`
    );
  }

  static searchByMaVatTu(term: string): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/vat-tu/tim-kiem/ma_vat_tu`,
      {
        params: {
          s: term,
        },
      }
    );
  }

  static searchByMaVatTuV2(term: string): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/vat-tu/tim-kiem`,
      {
        params: {
          s: term,
        },
      }
    );
  }

  static getSearch({
    term,
    id_kho,
    id_kho_exclude,
    id_dm_vat_tu,
    loai_vat_tu,
  }: {
    term: string;
    id_kho?: number;
    id_kho_exclude?: number;
    id_dm_vat_tu?: number;
    loai_vat_tu?: number;
  }): Promise<{
    data: ApiResponse<IVatTuResponseBase[] | IVatTuSearchInKhoResponseBase[]>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/vat-tu/tim-kiem",
      {
        params: {
          s: term,
          ...(id_kho && {
            id_kho,
          }),
          ...(id_kho_exclude && {
            id_kho_exclude,
          }),
          ...(id_dm_vat_tu && {
            id_dm_vat_tu,
          }),
          ...(typeof loai_vat_tu !== "undefined" && {
            loai_vat_tu,
          }),
        },
      }
    );
  }

  static getVatTuInKho({
    idKho,
    vat_tu_ids,
    perPage,
    thu_hoi,
    cong_ty_duyet,
  }: {
    idKho: number;
    vat_tu_ids?: number[];
    perPage?: number;
    thu_hoi?: 0 | 1;
    cong_ty_duyet?: 0 | 1;
  }): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuTrongKhoResponseBase>>;
  }> {
    let paramVatuTuArr = "";

    if (vat_tu_ids && vat_tu_ids.length) {
      paramVatuTuArr = vat_tu_ids.map((id) => `id_vat_tu[]=${id}`).join("&");
    }

    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/kho/chi-tiet/${idKho}/vat-tu?${paramVatuTuArr}`,
      {
        params: {
          ...(typeof thu_hoi !== "undefined" &&
            [0, 1].includes(thu_hoi) && {
              thu_hoi,
            }),
          ...(typeof cong_ty_duyet !== "undefined" &&
            [0, 1].includes(cong_ty_duyet) && {
              cong_ty_duyet,
            }),
        },
        headers: {
          perpage: perPage || 1000,
        },
      }
    );
  }

  static delete(id: number) {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/vat-tu/xoa/${id}`
    );
  }

  static getVatTuByHopDongId({
    current_page,
    id,
    per_page,
    s,
  }: {
    s?: string;
    id: number;
  } & IPaginationParams): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuHopDongResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/hop-dong/${id}/vat-tu`,
      {
        params: {
          page: current_page || 1,
          ...(s && {
            s,
          }),
        },
        headers: {
          perpage: per_page || 30,
        },
      }
    );
  }

  static getVatTuItemsByDinhMucCoDinhId({
    id_dinh_muc,
    current_page,
    per_page,
    s,
  }: IPaginationParams & {
    id_dinh_muc: number;
    s?: string;
  }): Promise<{
    data: ApiResponse<PaginationResponseBase<IVatTuDinhMucCoDinhResponseBase>>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      `/api/v1/dinh-muc-co-dinh/${id_dinh_muc}/vat-tu`,
      {
        params: {
          page: current_page || 1,
          ...(s && {
            s,
          }),
        },
        headers: {
          perpage: per_page || 30,
        },
      }
    );
  }

  static generateMaVatTu(id_danh_muc?: number): Promise<{
    data: ApiResponse<{
      ma_vat_tu: string;
    }>;
  }> {
    return apiServiceInstance().vueInstance.axios.get(
      "/api/v1/vat-tu/generate-ma-vat-tu",
      {
        params:
          typeof id_danh_muc === "number" && id_danh_muc > 0
            ? { id_danh_muc }
            : undefined,
      }
    );
  }

  static approve(id: number) {
    return apiServiceInstance().vueInstance.axios.post(
      `/api/v1/vat-tu/duyet/${id}`
    );
  }

  static approveVatTuNgoaiDinhMuc(vatTuIds: number[]) {
    return apiServiceInstance().vueInstance.axios.post(
      "/api/v1/vat-tu/duyet-ngoai-dinh-muc",
      {
        vat_tu_ids: vatTuIds,
      }
    );
  }
}
